import {
	Box,
	Divider,
	IconButton,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	TableSortLabel,
} from '@mui/material';
import { ToolsListing } from '../../../components/custom';
import { LayoutBase } from '../../../layouts';
import { VMenu } from '../../../components/shared';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { ServicesRisrot } from '../../../services/api/reports/ReportsServicesRisrot';
import { formatDateWhitoutHour, helpers } from '../../../services/helpers/helpers';
import { ArrowForward } from '@mui/icons-material';

import { useAuthContext } from '../../../context';
// import { NivelAcesso, SubNivelAcesso } from '../../../services/api/users/UserServices';

type OrderBy = 'data_inspecao' | 'data_criacao';
type OrderDirection = 'asc' | 'desc';

export default function Risrot() {
	const url = useParams();
	const navigate = useNavigate();
	const [data, setData] = useState<any[] | null>(null);
	const [loadin, setLoadin] = useState<boolean>(false);
	const [disabled, setDisabled] = useState<boolean>(false);

	// ordenação inicial: Data de inspeção, mais recente primeiro
	const [orderBy, setOrderBy] = useState<OrderBy>('data_inspecao');
	const [orderDirection, setOrderDirection] = useState<OrderDirection>('desc');

	const { userData } = useAuthContext();

	const pagePermissionsString: string | undefined = useMemo(() => {
		return userData?.nivel_acesso?.registros?.RISROT;
	}, [userData]);

	const hasActionPermission = (action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
		if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
			const actions = pagePermissionsString.split(',').map(s => s.trim());
			return actions.includes(action);
		}
		return false;
	};

	async function fetchData(uuid: string) {
		setLoadin(true);
		const response = await ServicesRisrot.list(uuid);
		if (response instanceof Error) {
			console.log('Error', response.message);
			setLoadin(false);
		} else {
			setData(response);
			console.log('response', response);
			setLoadin(false);
		}
	}

	useEffect(() => {
		if (url.id) {
			fetchData(url.id);
		}
	}, [url.id]);

	const handleViewEditReport = (uuidReport: string) => {
		const isReadOnlyMode = !hasActionPermission('edit');
		navigate(`./${uuidReport}`, { state: { isReadOnlyMode } });
	};

	async function pdf(uuid: string) {
		setDisabled(true);
		try {
			const response = await ServicesRisrot.getPDF(uuid);

			if (response instanceof Error) {
				console.error('Erro ao baixar o PDF');
				setDisabled(false);
				return;
			}

			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = url;
			link.download = 'RISROT.pdf';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			setDisabled(false);
		} catch (error) {
			console.error('Erro inesperado:', error);
			setDisabled(false);
		}
	}

	const handleRequestSort = (column: OrderBy) => {
		const isAsc = orderBy === column && orderDirection === 'asc';
		setOrderBy(column);
		setOrderDirection(isAsc ? 'desc' : 'asc');
	};

	const parseDate = (value: string | null | undefined) => {
		if (!value) return 0;
		const timestamp = new Date(value).getTime();
		return isNaN(timestamp) ? 0 : timestamp;
	};

	const sortedData = useMemo(() => {
		if (!data) return [];

		return [...data].sort((a, b) => {
			const aValue = parseDate(a[orderBy]);
			const bValue = parseDate(b[orderBy]);

			if (orderDirection === 'asc') {
				return aValue - bValue;
			}

			return bValue - aValue;
		});
	}, [data, orderBy, orderDirection]);

	return (
		<LayoutBase
			title='Relatórios de Inspeção de Segurança Rotineiro'
			tollBar={
				<ToolsListing
					textoBotaoNovo='Novo'
					showNewButton={pagePermissionsString?.includes('add')}
					showBackButton
					mostrarInputBusca={false}
					onClickNew={() => {
						if (!hasActionPermission('add')) {
							console.warn('Tentativa de criar novo relatório sem permissão de "add".');
							return;
						}
						navigate('./new');
					}}
				/>
			}
		>
			<Box>
				<VMenu id={url.id} />
				<Divider />
				<Box padding={2}>
					{loadin === true ? (
						<LinearProgress />
					) : (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Box>
								<Typography>Relatórios Anteriores</Typography>
								<Divider />
							</Box>

							<Box padding={1}>
								<Table component={Paper} variant='outlined' square={false} sx={{ border: 0 }}>
									<TableHead>
										<TableRow>
											<TableCell align='center'>
												<TableSortLabel
													active={orderBy === 'data_inspecao'}
													direction={orderBy === 'data_inspecao' ? orderDirection : 'desc'}
													onClick={() => handleRequestSort('data_inspecao')}
												>
													Data de inspeção
												</TableSortLabel>
											</TableCell>

											<TableCell align='center'>Responsáveis</TableCell>
											<TableCell align='center'>Criado por</TableCell>

											<TableCell align='center'>
												<TableSortLabel
													active={orderBy === 'data_criacao'}
													direction={orderBy === 'data_criacao' ? orderDirection : 'desc'}
													onClick={() => handleRequestSort('data_criacao')}
												>
													Criado em
												</TableSortLabel>
											</TableCell>

											<TableCell align='center'>Ações</TableCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{sortedData.map((item) => (
											<TableRow key={item.uuid}>
												<TableCell align='center'>
													{helpers.convertDateManual(item.data_inspecao)}
												</TableCell>
												<TableCell align='center'>{item.responsavel_inpecao}</TableCell>
												<TableCell align='center'>
													{item.criado_por ? item.criado_por : '----'}
												</TableCell>
												<TableCell align='center'>
													{item.data_criacao ? formatDateWhitoutHour(item.data_criacao) : '----'}
												</TableCell>
												<TableCell align='center'>
													{(hasActionPermission('view') || hasActionPermission('edit')) && (
														<IconButton onClick={() => handleViewEditReport(item.uuid)}>
															<ArrowForward />
														</IconButton>
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</LayoutBase>
	);
}